import { getOccupationZones } from "@/services/occupations";
import { Occupation } from "@/services/occupations/types/occupations.types";
import { updateUserInfo } from "@/services/users";
import {
  PROFESSIONAL_OF_WORK_ZONE_ID,
  PROFESSIONAL_OF_WORK_ZONE_LABEL,
  TIME_OF_PROFESSION_OPTIONS,
} from "@/utils/profession.constants";
import { getOnlyDigits, isValidCpf } from "@/utils/cpf";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useLoader } from "@/context/LoaderContext";
import { queryClient } from "@/services/react-query";
import { invalidateRegistrationProgressQueries } from "@/services/inscription/invalidateRegistrationProgressQueries";
import { syncPdcMemberWithRetries } from "@/services/pdc";
import { useUser } from "@/context/UserContext";
import { ensurePdcEmbedApiKey } from "@/utils/pdcEmbed";

const PDC_SYNC_LOADING_MESSAGE =
  "Liberando seu acesso aos cursos. Isso pode levar alguns instantes…";

export const useCompleteRegistrationForm = () => {
  const loader = useLoader();
  const { userInfo } = useUser();

  const [occupations, setOccupations] = useState<Occupation[]>([]);
  const [cpf, setCpf] = useState("");
  const [selectedOccupationId, setSelectedOccupationId] = useState("");
  const [timeOfProfession, setTimeOfProfession] = useState("");
  const [imageUrl, setImageUrl] = useState(userInfo?.imageUrl ?? "");

  useEffect(() => {
    setImageUrl(userInfo?.imageUrl ?? "");
  }, [userInfo?.imageUrl]);

  useEffect(() => {
    const loadOccupationData = async () => {
      try {
        const data = await getOccupationZones();
        setOccupations(data.occupation ?? []);
      } catch (error) {
        console.error("Erro ao carregar áreas/profissões", error);
        toast("Erro ao carregar áreas de atuação e profissões.", { type: "error" });
      }
    };

    void loadOccupationData();
  }, []);

  const filteredOccupations = useMemo(() => {
    return occupations.filter((occupation) =>
      occupation.zoneId.includes(PROFESSIONAL_OF_WORK_ZONE_ID)
    );
  }, [occupations]);

  const cpfError = useMemo(() => {
    const cpfDigits = getOnlyDigits(cpf);

    if (cpfDigits.length === 0) return "";
    if (!isValidCpf(cpfDigits)) return "CPF inválido.";

    return "";
  }, [cpf]);

  const isValid = useMemo(() => {
    const cpfDigits = getOnlyDigits(cpf);

    return (
      !!imageUrl?.trim() &&
      cpfDigits.length === 11 &&
      isValidCpf(cpfDigits) &&
      !!selectedOccupationId &&
      !!timeOfProfession
    );
  }, [cpf, selectedOccupationId, timeOfProfession, imageUrl]);

  const onSubmit = async () => {
    if (!isValid) {
      return toast("Preencha todos os campos corretamente", { type: "error" });
    }

    try {
      loader.show("complete_registration");
      const professionName =
        occupations.find((o) => o.id === Number(selectedOccupationId))?.name ?? "";

      await updateUserInfo({
        imageUrl,
        cpf: getOnlyDigits(cpf),
        occupationField: PROFESSIONAL_OF_WORK_ZONE_LABEL,
        profession: professionName,
        timeOfProfession,
      });

      // Refetch antes do toast e antes do PDC: o modal usa `needsCompleteRegistration(userInfo)` do cache;
      // se o toast vinha primeiro e o refetch só depois do sync PDC, o Dialog ficava aberto até o fim do sync.
      await queryClient.refetchQueries({ queryKey: ["user-info"], exact: false });

      // Dois frames: deixa o React/MUI fechar o Dialog antes do toast (UX pedida).
      await new Promise<void>((resolve) => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => resolve());
        });
      });

      loader.hide("complete_registration");

      toast("Cadastro concluído!", { type: "success" });

      loader.show("pdc_sync", PDC_SYNC_LOADING_MESSAGE);

      try {
        const syncRes = await syncPdcMemberWithRetries();
        if (syncRes.synced && userInfo?.id != null) {
          ensurePdcEmbedApiKey(userInfo.id);
        } else if (!syncRes.synced) {
          console.warn("[PDC] sync after registration did not complete", syncRes);
        }
      } catch (syncErr) {
        console.warn("[PDC] sync after registration failed", syncErr);
      }
      // Com lista PDC: habilita GET /external/courses e alimenta dashboard + merge de inscrição (useRegistrationCompletion).
      await invalidateRegistrationProgressQueries();
      window.location.reload();
    } catch (err) {
      console.error(err);
      toast("Erro ao salvar dados. Tente novamente.", { type: "error" });
    } finally {
      loader.hide("complete_registration");
      loader.hide("pdc_sync");
    }
  };

  return {
    imageUrl,
    setImageUrl,
    cpf,
    setCpf,
    cpfError,
    filteredOccupations,
    selectedOccupationId,
    setSelectedOccupationId,
    timeOfProfession,
    setTimeOfProfession,
    isValid,
    onSubmit,
    timeOfProfessionOptions: TIME_OF_PROFESSION_OPTIONS,
  };
};
